import { useState } from 'react';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import H4 from '@sdk/components/ui/typography/H4';
import gettext, { ngettext } from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import Row from '@sdk/components/ui/spacing/Row';
import Column from '@sdk/components/ui/spacing/Column';
import Table, { TableHeader, TableHeaderCell } from '@sdk/components/ui/table';
import useOrderedData from '@sdk/utils/hooks/useOrderedData';
import HorizontalSeparator from '@sdk/components/ui/HorizontalSeparator';
import StatusLabel, { StatusLabelType } from '@sdk/components/ui/StatusLabel';
import useAuth from '@sdk/authentication/useAuth';
import { DocumentData } from '@sdk/users/types/DocumentData';

import PlusIcon from '@web/assets/svg/UI/plus-white.svg';
import { FoldableSearch } from '@web/components/FoldableSearch/FoldableSearch';
import EmptyDocumentsState from '@web/users/documents/EmptyDocumentsState/EmptyDocumentsState';
import DocumentListItem from '@web/users/documents/DocumentListItem/DocumentListItem';
import {
  TopBar,
  AddDocumentsButton,
} from '@web/users/documents/DocumentsList/documentsList.styles';
import { getExampleItem } from '@web/users/documents/DocumentsList/documentsList.utils';

type Props = {
  onAdd: () => void;
  onEdit: (document: DocumentData) => void;
  onRemove: () => void;
  documentsList: DocumentData[];
  isUserManagedByMe: boolean;
  disabled: boolean;
};

const DocumentsList = ({
  onAdd,
  onEdit,
  onRemove,
  documentsList,
  isUserManagedByMe,
  disabled,
}: Props) => {
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const { isDescending, changeOrder, orderProp, orderedData } =
    useOrderedData<DocumentData>(documentsList);

  let filteredData = orderedData?.filter((dataItem) => {
    if (!searchTerm) {
      return true;
    }

    return (
      dataItem.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      dataItem.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  if (disabled) {
    filteredData = [getExampleItem(currentUser)];
  }

  const documentsCount = disabled ? 1 : filteredData?.length || 0;

  return (
    <>
      <TopBar>
        <H4 themeColor={ThemeColorType.primary}>
          {ngettext('%(count)s Document', '%(count)s Documents', documentsCount, {
            count: documentsCount,
          })}
        </H4>
        {disabled && (
          <Column mLeft={10}>
            <StatusLabel label={gettext('Example')} type={StatusLabelType.info} />
          </Column>
        )}
        {Boolean(documentsCount || searchTerm) && (
          <Row mLeft="auto" alignItems="center">
            <FoldableSearch onSubmitSearch={setSearchTerm} submitEager transparentBackground />
          </Row>
        )}
        {isUserManagedByMe && (
          <AddDocumentsButton
            buttonType={ButtonType.primary}
            onClick={onAdd}
            iconLeft={<PlusIcon />}
            noPadding>
            <Row mRight={10}>{gettext('Add document')}</Row>
          </AddDocumentsButton>
        )}
      </TopBar>
      <HorizontalSeparator />
      {!documentsCount && <EmptyDocumentsState isSearch={!!searchTerm} />}
      {Boolean(documentsCount) && (
        <>
          <HorizontalSeparator themeColor={ThemeColorType.separatorOnGray} />
          <Table>
            <TableHeader>
              <TableHeaderCell
                label={gettext('Name')}
                orderProp="name"
                changeOrder={changeOrder}
                isDescending={isDescending}
                currentOrderProp={orderProp}
              />
              <TableHeaderCell
                label={gettext('Description')}
                orderProp="description"
                changeOrder={changeOrder}
                isDescending={isDescending}
                currentOrderProp={orderProp}
              />
              <TableHeaderCell
                label={gettext('Expiration date')}
                orderProp="expirationDate"
                changeOrder={changeOrder}
                isDescending={isDescending}
                currentOrderProp={orderProp}
              />
              <TableHeaderCell
                label={gettext('Last modified')}
                orderProp="lastModifiedBy.fullname"
                changeOrder={changeOrder}
                isDescending={isDescending}
                currentOrderProp={orderProp}
              />
              <TableHeaderCell alignRight width={80} label={gettext('Options')} />
            </TableHeader>
            <tbody>
              {filteredData.map((documentData) => (
                <DocumentListItem
                  data={documentData}
                  key={documentData.id}
                  onRemoved={onRemove}
                  onEdit={onEdit}
                />
              ))}
            </tbody>
          </Table>
        </>
      )}
    </>
  );
};

export default DocumentsList;
